"use client";

import { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Search, Wrench, MapPin, Star, Code as Code2, ChartBar as BarChart3, ArrowRight, CircleCheck as CheckCircle2, Sparkles } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP, ScrollTrigger);
export default function Methodology() {
  const containerRef = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const el = containerRef.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".process-card", {
        y: 35
      });
      gsap.to(".process-card", {
        opacity: 1,
        y: 0,
        duration: 0.65,
        stagger: 0.12,
        ease: "power2.out",
        scrollTrigger: {
          trigger: el,
          start: "top 80%",
          once: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set(".process-card", {
        opacity: 1,
        y: 0
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: containerRef
  });
  const steps = [{
    number: "01",
    title: "Comprehensive SEO Audit",
    phase: "Audit",
    description: "I perform a forensic, hands-on audit of your technical infrastructure, backlink profile, search intent, and competitor positions to pinpoint your exact growth opportunities.",
    icon: Search,
    deliverables: ["Technical health diagnostics", "Keyword & content gap map", "Actionable priority roadmap"]
  }, {
    number: "02",
    title: "Strategy & Technical Optimization",
    phase: "Optimization",
    description: "I resolve site speed issues, crawl traps, and site architecture while optimizing high-intent pages for both traditional Google algorithms and modern AI engines.",
    icon: Wrench,
    deliverables: ["Core Web Vitals tuning", "On-page search architecture", "Content optimization"]
  }, {
    number: "03",
    title: "Citations & Local Authority",
    phase: "Citations",
    description: "For UK and regional businesses, I establish clean directory listings, strengthen geo-targeted signals, and build authoritative backlinks that prove real-world relevance.",
    icon: MapPin,
    deliverables: ["NAP consistency audit", "Premium directory placements", "Niche citation building"]
  }, {
    number: "04",
    title: "Review & Reputation Growth",
    phase: "Reviews",
    description: "I design strategies to capture genuine customer feedback, accelerating review velocity across Google Business Profile and trust platforms to boost conversion rates.",
    icon: Star,
    deliverables: ["Review generation funnel", "Trust signal optimization", "Reputation monitoring"]
  }, {
    number: "05",
    title: "Structured Data & Schema",
    phase: "Schema",
    description: "I implement precise Schema.org markup so search engines and AI engines (ChatGPT, SearchGPT, Perplexity) fully comprehend your brand entities and service offerings.",
    icon: Code2,
    deliverables: ["Rich snippet implementation", "Entity linking schema", "AI-ready knowledge graphs"]
  }, {
    number: "06",
    title: "Transparent Revenue Reporting",
    phase: "Reporting",
    description: "No vague metrics or vanity statistics. I deliver clear monthly reports focusing on keyword rankings, qualified leads, organic traffic quality, and commercial business impact.",
    icon: BarChart3,
    deliverables: ["Custom live dashboard", "Direct strategy call with me", "Monthly priority updates"]
  }];
  return <section id="methodologyprocessflow" data-nav="light" className="relative py-20 lg:py-28 bg-background text-foreground overflow-hidden">
      <div className="absolute top-1/4 left-0 -translate-x-1/2 w-96 h-96 bg-secondary/40 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-10 right-0 translate-x-1/3 w-80 h-80 bg-accent/20 rounded-full blur-3xl pointer-events-none" />

      <div ref={containerRef} className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-3xl mx-auto text-center mb-16 lg:mb-20">
          <Badge variant="secondary" className="mb-4 px-3.5 py-1 text-xs sm:text-sm font-medium border border-accent/30 text-foreground">
            <Sparkles className="size-3.5 text-accent mr-1.5" />
            Direct Consultant Delivery
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground font-serif">
            My Proven 6-Step SEO Methodology
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            You work directly with me—Yassine Essaadi. Here is the structured, step-by-step process I execute to deliver sustainable search visibility and commercial growth.
          </p>
        </div>

        <div className="relative">
          <div className="hidden lg:block absolute top-1/2 left-8 right-8 h-0.5 bg-accent/30 -translate-y-1/2 pointer-events-none" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 relative">
            {steps.map((step, i) => {
            const Icon = step.icon;
            return <Card key={step.number} data-index={i} className="process-card relative bg-card text-card-foreground border border-border shadow-md rounded-2xl transition-all duration-300 hover:shadow-xl hover:border-accent/50 flex flex-col justify-between" style={{
              opacity: 0
            }}>
                  <div className="absolute top-0 left-6 right-6 h-1 bg-secondary rounded-b-md" />

                  <CardHeader className="pt-8 pb-4">
                    <div className="flex items-center justify-between mb-4">
                      <div className="size-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg shadow-md shrink-0">
                        {step.number}
                      </div>

                      <Badge variant="outline" className="border-accent/40 bg-secondary/50 text-foreground font-medium text-xs px-2.5 py-0.5">
                        {step.phase}
                      </Badge>
                    </div>

                    <div className="flex items-center gap-2 mb-2">
                      <Icon className="size-5 text-primary shrink-0" />
                      <CardTitle className="text-xl font-bold font-serif text-card-foreground">
                        {step.title}
                      </CardTitle>
                    </div>
                  </CardHeader>

                  <CardContent className="space-y-4 pt-0">
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {step.description}
                    </p>

                    <div className="pt-2 border-t border-border/60">
                      <span className="text-xs font-semibold text-foreground uppercase tracking-wider block mb-2">
                        Key Deliverables:
                      </span>
                      <ul className="space-y-1.5">
                        {step.deliverables.map((item, idx) => <li key={idx} className="text-xs text-muted-foreground flex items-center gap-2">
                            <CheckCircle2 className="size-3.5 text-accent shrink-0" />
                            <span>{item}</span>
                          </li>)}
                      </ul>
                    </div>
                  </CardContent>
                </Card>;
          })}
          </div>
        </div>

        <div className="mt-16 lg:mt-20 p-8 sm:p-10 rounded-2xl bg-card border border-border shadow-lg max-w-4xl mx-auto text-center relative overflow-hidden">
          <div className="absolute -top-12 -right-12 size-40 bg-accent/20 rounded-full blur-2xl pointer-events-none" />
          
          <h3 className="text-2xl sm:text-3xl font-bold font-serif text-foreground mb-3">
            Ready to grow your search visibility directly with an expert?
          </h3>
          <p className="text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto mb-6 leading-relaxed">
            I limit the number of clients I accept to guarantee direct personal oversight on every audit, implementation, and strategy call.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-xl px-7 shadow-md font-medium w-full sm:w-auto">
              <Link to="/ContactPage">
                Book a Free SEO Consultation
                <ArrowRight className="size-4 ml-1.5" />
              </Link>
            </Button>

            <Button asChild variant="outline" size="lg" className="border-border hover:bg-secondary text-foreground rounded-xl px-6 font-medium w-full sm:w-auto">
              <Link to="/CaseStudiesPage">
                Explore My Case Studies
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}